import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const BASE_URL = 'https://birdseye-main-326804163209.asia-southeast1.run.app';
const REQUEST_TIMEOUT_MS = 3500;

const fetchWithTimeout = (url, options = {}, timeoutMs = REQUEST_TIMEOUT_MS) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  return fetch(url, { ...options, signal: controller.signal })
    .finally(() => clearTimeout(timeoutId));
};

// ─── Nav Icons ─────────────────────────────────────────────────────────────────
const navIcons = {
  Dashboard: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  Cameras: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
      <circle cx="12" cy="13" r="4"/>
    </svg>
  ),
  History: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="12 8 12 12 14 14"/>
      <path d="M3.05 11a9 9 0 1 0 .5-4.5"/><polyline points="3 3 3 7 7 3"/>
    </svg>
  ),
  Footage: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
      <path d="M10 11l5 3-5 3v-6z"/>
    </svg>
  ),
  Profile: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
    </svg>
  ),
  Settings: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3"/>
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
    </svg>
  ),
  Team: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <circle cx="9" cy="7" r="3" />
      <path d="M3 20c1.5-3 4-4.5 6-4.5s4.5 1.5 6 4.5" />
      <path d="M17 11c1 0 2 .8 2 2v1" />
    </svg>
  ),
  Logout: (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
};

const BellIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);
const CameraIcon = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);
const AlertTriangle = () => (
  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);
const ChevronDownIcon = ({ open }) => (
  <svg className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);

// ─── Toggle Switch ─────────────────────────────────────────────────────────────
const Toggle = ({ enabled, onChange, disabled }) => (
  <button
    onClick={() => !disabled && onChange(!enabled)}
    disabled={disabled}
    className={`relative w-11 h-6 rounded-full transition-colors duration-300 focus:outline-none flex-shrink-0
      ${enabled ? 'bg-[#AC4242]' : 'bg-gray-200'}
      ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}`}
  >
    <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform duration-300 ${enabled ? 'translate-x-5' : 'translate-x-0'}`} />
  </button>
);

// ─── Section Card ──────────────────────────────────────────────────────────────
const SectionCard = ({ icon, title, children, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay, ease: [0.25, 0.1, 0.25, 1] }}
    className="bg-white rounded-3xl border border-black/[0.05] overflow-visible shadow-sm"
  >
    <div className="px-6 pt-5 pb-4 flex items-center gap-3 border-b border-gray-100">
      <div className="w-8 h-8 rounded-xl bg-[#FDF0F0] flex items-center justify-center text-[#AC4242] flex-shrink-0">
        {icon}
      </div>
      <p className="font-poppins font-bold text-gray-800 text-sm tracking-tight">{title}</p>
    </div>
    <div className="px-6 py-2">{children}</div>
  </motion.div>
);

// ─── Toggle Row ────────────────────────────────────────────────────────────────
const ToggleRow = ({ label, description, enabled, onChange, disabled, tag }) => (
  <div className="flex items-center justify-between gap-4 py-4 border-b border-gray-100 last:border-0">
    <div>
      <div className="flex items-center gap-2">
        <p className="font-poppins font-semibold text-sm text-gray-800">{label}</p>
        {tag && <span className="text-[10px] font-poppins font-bold px-2 py-0.5 rounded-full bg-gray-100 text-gray-400">{tag}</span>}
      </div>
      {description && <p className="font-poppins text-xs text-gray-400 mt-0.5">{description}</p>}
    </div>
    <Toggle enabled={enabled} onChange={onChange} disabled={disabled} />
  </div>
);

// ─── Custom Select ─────────────────────────────────────────────────────────────
const CustomSelect = ({ value, options, onChange }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-2 px-3 py-1.5 bg-[#FDF0F0] border border-[#AC4242]/15 rounded-xl text-sm font-poppins font-semibold text-[#AC4242] hover:bg-[#f9e4e4] transition-colors min-w-[120px] justify-between"
      >
        {value}
        <ChevronDownIcon open={open} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -6, scale: 0.97 }}
            transition={{ duration: 0.15 }}
            className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-30 overflow-hidden min-w-[150px]"
          >
            {options.map(opt => (
              <button
                key={opt}
                onClick={() => { onChange(opt); setOpen(false); }}
                className={`w-full text-left px-4 py-2.5 text-sm font-poppins hover:bg-[#FDF0F0] transition-colors ${value === opt ? 'text-[#AC4242] font-semibold bg-[#FDF0F0]' : 'text-gray-700'}`}
              >
                {opt}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─── Inline Editable ──────────────────────────────────────────────────────────
const InlineEdit = ({ value, onSave, placeholder }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);

  useEffect(() => { setDraft(value); }, [value]);

  const save = () => { onSave(draft); setEditing(false); };
  const cancel = () => { setDraft(value); setEditing(false); };

  if (editing) return (
    <div className="flex items-center gap-2">
      <input
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') cancel(); }}
        placeholder={placeholder}
        className="border border-[#AC4242]/25 rounded-xl px-3 py-1.5 font-poppins text-sm text-gray-800 outline-none focus:ring-2 focus:ring-[#AC4242]/15 placeholder-gray-300 bg-[#FDFAF9] w-40"
      />
      <button onClick={save} className="bg-[#AC4242] text-white rounded-lg px-3 py-1.5 text-xs font-poppins font-semibold hover:bg-[#943636] transition-colors">Save</button>
      <button onClick={cancel} className="text-gray-400 text-xs font-poppins hover:text-gray-600">Cancel</button>
    </div>
  );
  return (
    <button
      onClick={() => { setDraft(value); setEditing(true); }}
      className="px-3 py-1.5 bg-[#FDF0F0] border border-[#AC4242]/15 rounded-xl text-sm font-poppins font-semibold text-[#AC4242] hover:bg-[#f9e4e4] transition-colors"
    >
      {value || <span className="text-[#AC4242]/50">{placeholder}</span>}
    </button>
  );
};

// ─── Toast ────────────────────────────────────────────────────────────────────
const Toast = ({ message, type = 'success', onDismiss }) => (
  <motion.div
    initial={{ opacity: 0, x: 60 }}
    animate={{ opacity: 1, x: 0 }}
    exit={{ opacity: 0, x: 60 }}
    className="fixed bottom-6 right-6 z-50 flex items-center gap-3 bg-white border border-black/[0.08] rounded-2xl px-4 py-3 shadow-xl"
  >
    <span className={`w-7 h-7 rounded-xl flex items-center justify-center flex-shrink-0 ${type === 'success' ? 'bg-emerald-50 text-emerald-500' : 'bg-[#FDF0F0] text-[#AC4242]'}`}>
      {type === 'success' ? (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      ) : <AlertTriangle />}
    </span>
    <p className="font-poppins font-semibold text-xs text-gray-800">{message}</p>
    <button onClick={onDismiss} className="ml-2 text-gray-300 hover:text-gray-500 transition-colors">
      <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
        <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    </button>
  </motion.div>
);

// ─── Settings Page ─────────────────────────────────────────────────────────────
const Settings = () => {
  const navigate = useNavigate();

  // ── Notification state ──
  const [criticalNotifs, setCriticalNotifs] = useState(() => {
    const stored = localStorage.getItem('criticalNotifs');
    return stored === null ? true : stored === 'true';
  });

  // ── Camera state — seed from localStorage so navigating back shows last saved value ──
  const [camera1Label,   setCamera1Label]   = useState(() => localStorage.getItem('camera1Label') || 'Zone 1');
  const [orientation,    setOrientation]    = useState(() => localStorage.getItem('camera1Orientation') || 'Default');

  const [refreshing, setRefreshing] = useState(false);
  const [toast,      setToast]      = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // ── GET: load settings on mount ──────────────────────────────────────────
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) return undefined;

    let cancelled = false;
    setRefreshing(true);

    fetchWithTimeout(`${BASE_URL}/users/settings`, {
      headers: { 'Authorization': `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then(data => {
        if (cancelled) return;
        console.log('[Settings] GET response:', data);
        const s = data.settings || data.profile || data || {};
        if (s.criticalNotifs !== undefined) {
          setCriticalNotifs(s.criticalNotifs);
          localStorage.setItem('criticalNotifs', String(s.criticalNotifs));
        }
        if (s.camera1Label) {
          setCamera1Label(s.camera1Label);
          localStorage.setItem('camera1Label', s.camera1Label);
        }
        if (s.orientation) {
          setOrientation(s.orientation);
          localStorage.setItem('camera1Orientation', s.orientation);
        }
      })
      .catch(err => console.error('[Settings] GET error:', err))
      .finally(() => {
        if (!cancelled) setRefreshing(false);
      });

    return () => { cancelled = true; };
  }, []);

  // ── PUT: save to backend ──────────────────────────────────────────────────
  const saveToBackend = async (patch) => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    const nextLabel       = patch.camera1Label   ?? camera1Label;
    const nextOrientation = patch.orientation    ?? orientation;

    const body = {
      settings: {
        criticalNotifs: patch.criticalNotifs  ?? criticalNotifs,
        camera1Label:   nextLabel,
        orientation:    nextOrientation,
      },
    };

    console.log('[Settings] PUT body:', body);
    try {
      const res = await fetch(`${BASE_URL}/users/change_user_settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      showToast('Settings saved!');

      // Write both camera values to localStorage so Dashboard reads them instantly
      localStorage.setItem('criticalNotifs', String(body.settings.criticalNotifs));
      localStorage.setItem('camera1Label',       nextLabel);
      localStorage.setItem('camera1Orientation', nextOrientation);
    } catch (err) {
      console.error('[Settings] PUT error:', err);
      showToast('Failed to save settings.', 'error');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userEmail');
    navigate('/login');
  };

  const navItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Cameras',   path: '/cameras'   },
    { label: 'History',   path: '/history'   },
    { label: 'Footage',   path: '/footage'   },
    { label: 'Team',      path: '/team' },
    { label: 'Profile',   path: '/profile'   },
    { label: 'Settings',  path: '/settings', active: true },
  ];

  const orientationOptions = ['Default', 'Rotate 90°', 'Rotate 180°', 'Rotate 270°', 'Flip H', 'Flip V'];

  return (
    <motion.div
      className="h-screen flex overflow-hidden bg-[#F5EFE7]"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2, ease: 'easeInOut' }}
    >
      <AnimatePresence>
        {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
      </AnimatePresence>

      {/* ── Sidebar ── */}
      <div className="w-56 flex-shrink-0 p-3">
        <div className="h-full bg-[#AC4242] rounded-3xl flex flex-col">
          <div className="p-6 flex justify-center">
            <img src="/images/logonav.png" alt="BirdsEye" className="h-32 w-auto" />
          </div>
          <nav className="flex-1 px-4 space-y-1">
            {navItems.map(item => (
              <button
                key={item.label}
                onClick={() => navigate(item.path)}
                className={`flex items-center gap-3 px-4 py-3 w-full text-left text-white rounded-xl font-poppins text-sm transition-all duration-200 hover:scale-[1.02]
                  ${item.active ? 'bg-white/20 font-semibold' : 'hover:bg-white/10'}`}
              >
                {navIcons[item.label]}
                {item.label}
              </button>
            ))}
          </nav>
          <div className="px-4 pb-6">
            <button onClick={handleLogout} className="flex items-center gap-3 px-4 py-3 w-full text-left text-white/60 hover:text-white hover:bg-white/10 rounded-xl font-poppins text-sm transition-all duration-200">
              {navIcons.Logout} Logout
            </button>
          </div>
        </div>
      </div>

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="bg-[#F5EFE7] border-b border-black/[0.07] px-8 py-4 flex items-center justify-between flex-shrink-0">
          <img src="/images/loginlogo.png" alt="BirdsEye" className="h-12 w-auto" />
          <span className="text-xs font-poppins text-gray-400">{localStorage.getItem('userEmail') || ''}</span>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-7">
          <h1 className="text-5xl font-poppins font-bold text-[#AC4242] tracking-tight mb-6">Settings</h1>

          {refreshing && (
            <p className="text-xs font-poppins text-gray-400 -mt-4 mb-4">Syncing latest settings...</p>
          )}

          <div className="max-w-3xl grid grid-cols-1 gap-5">

              {/* ── Alerts & Notifications ── */}
              <SectionCard icon={<BellIcon />} title="Alerts & Notifications" delay={0.05}>
                <ToggleRow
                  label="In-Device Critical Notifications"
                  description="Receive alerts within the app for critical events like high ammonia or wet litter"
                  enabled={criticalNotifs}
                  onChange={(val) => {
                    setCriticalNotifs(val);
                    saveToBackend({ criticalNotifs: val });
                  }}
                />
                <ToggleRow
                  label="Push Notifications"
                  description="Enable Critical SMS alerts to your mobile device"
                  enabled={true}
                  onChange={() => {}}
                />
              </SectionCard>

              {/* ── Camera Configuration ── */}
              <SectionCard icon={<CameraIcon />} title="Camera Configuration" delay={0.1}>

                {/* Camera label */}
                <div className="py-4 border-b border-gray-100 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-poppins font-semibold text-sm text-gray-800">Camera 1 Label</p>
                    <p className="font-poppins text-xs text-gray-400 mt-0.5">
                      This name appears on the live feed overlay in the Dashboard
                    </p>
                  </div>
                  <InlineEdit
                    value={camera1Label}
                    placeholder="e.g. Zone 1"
                    onSave={(v) => {
                      setCamera1Label(v);
                      saveToBackend({ camera1Label: v });
                    }}
                  />
                </div>

                {/* Orientation */}
                <div className="py-4 flex items-center justify-between gap-4">
                  <div>
                    <p className="font-poppins font-semibold text-sm text-gray-800">Camera 1 Orientation</p>
                    <p className="font-poppins text-xs text-gray-400 mt-0.5">
                      Rotate or flip the live feed if the camera is mounted at an angle
                    </p>
                  </div>
                  <CustomSelect
                    value={orientation}
                    options={orientationOptions}
                    onChange={(v) => {
                      setOrientation(v);
                      saveToBackend({ orientation: v });
                    }}
                  />
                </div>

              </SectionCard>

          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default Settings;
